import * as React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '@/hooks/useAuth';
import { api, getErrorMessage } from '@/api/api';
import accountIcon from '@/assets/images/svg/account_icon.svg';
import editIcon from '@/assets/images/svg/edit_icon.svg';
import clsx from 'clsx';
import { useToast } from '@/context/ToastContext';
import type { User } from '@/types';

// Import New Components
import { PhoneInput } from './settings/shared/PhoneInput';
import { CountrySelect } from './settings/shared/CountrySelect';
import { BottomDrawer } from '@/components/ui/BottomDrawer';
import { Button } from '@/components/ui/Button';

const profileSchema = z.object({
  first_name: z.string().min(1, 'First name is required').max(100),
  last_name: z.string().min(1, 'Last name is required').max(100),
  date_of_birth: z
    .string()
    .nullable()
    .optional()
    .refine((val) => {
      if (!val) return true;
      const date = new Date(val as string);
      return !isNaN(date.getTime()) && date < new Date();
    }, 'Date of birth must be in the past')
    .refine((val) => {
      if (!val) return true;
      const date = new Date(val as string);
      if (isNaN(date.getTime())) return true;
      const today = new Date();
      const age =
        today.getFullYear() -
        date.getFullYear() -
        (today.getMonth() < date.getMonth() ||
        (today.getMonth() === date.getMonth() && today.getDate() < date.getDate())
          ? 1
          : 0);
      return age >= 13;
    }, 'You must be at least 13 years old')
    .refine((val) => {
      if (!val) return true;
      const date = new Date(val as string);
      if (isNaN(date.getTime())) return true;
      const today = new Date();
      const age =
        today.getFullYear() -
        date.getFullYear() -
        (today.getMonth() < date.getMonth() ||
        (today.getMonth() === date.getMonth() && today.getDate() < date.getDate())
          ? 1
          : 0);
      return age <= 100;
    }, 'Please enter a valid date of birth'),
  phone_number: z.string().max(20, 'Phone number too long').nullable().optional(),
  country: z.string().max(100).nullable().optional(),
  city: z.string().max(100).nullable().optional(),
  high_school: z.string().max(200).nullable().optional(),
  university: z.string().max(200).nullable().optional(),
  bio: z.string().max(1000, 'Bio must be under 1000 characters').nullable().optional(),
});

type ProfileFormValues = z.infer<typeof profileSchema>;

/**
 * Helper to format date string (YYYY-MM-DD) to DD/MM/YYYY
 */
const formatDateDisplay = (dateString: string | null | undefined) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return new Intl.DateTimeFormat('en-GB').format(date);
  } catch {
    return dateString;
  }
};

export const InfoGrid: React.FC = () => {
  const { user, updateUser } = useAuth();
  const { addToast } = useToast();
  const [isEditing, setIsEditing] = React.useState(false);
  const [apiError, setApiError] = React.useState<string | null>(null);

  // React Hook Form Setup
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      date_of_birth: '',
      phone_number: '',
      country: '',
      city: '',
      high_school: '',
      university: '',
      bio: '',
    },
  });

  // Sync form with user data when user loads or edit mode opens
  React.useEffect(() => {
    if (user) {
      const firstName = user.first_name ?? '';
      const lastName = user.last_name ?? '';

      reset({
        first_name: firstName,
        last_name: lastName,
        date_of_birth: user.date_of_birth || '',
        phone_number: user.phone_number || '',
        country: user.country || '',
        city: user.city || '',
        high_school: user.high_school || '',
        university: user.university || '',
        bio: user.bio || '',
      });
    }
  }, [user, isEditing, reset]);

  const onSubmit = async (data: ProfileFormValues) => {
    setApiError(null);
    try {
      // Revert: send fields directly without joining names
      const payload: Partial<User> = {
        ...Object.fromEntries(
          Object.entries(data).map(([key, val]) => [key, val === '' ? null : val])
        ),
      };

      const updatedUser = await api.users.updateMe(payload);
      updateUser(updatedUser);
      setIsEditing(false);

      // Success toast
      addToast({
        title: 'Profile Updated',
        subtitle: 'Your account information has been saved.',
        icon: user?.profile_picture_url || accountIcon,
        type: 'success',
      });
    } catch (err) {
      const msg = getErrorMessage(err);
      setApiError(msg);

      // Error toast with actions
      addToast({
        title: 'Update Failed',
        subtitle: "We couldn't save your changes.",
        type: 'error',
        action: {
          label: 'Retry',
          onClick: () => handleSubmit(onSubmit)(),
        },
        secondaryAction: {
          label: 'Dismiss',
          onClick: () => {
            /* dismiss */
          },
        },
      });
    }
  };

  // Helper for Input Fields
  const InputField = ({
    label,
    field,
    value,
    disabled = false,
    type = 'text',
    responsiveRight = false,
    min,
    max,
  }: {
    label: string;
    field: keyof ProfileFormValues | 'email';
    value: string | null | undefined;
    disabled?: boolean;
    type?: string;
    responsiveRight?: boolean;
    min?: string;
    max?: string;
  }) => {
    const isEmail = field === 'email';
    // Determine display value for read-only mode
    const displayValue = field === 'date_of_birth' ? formatDateDisplay(value) : value || '-';

    const errorMsg = !isEmail && errors[field as keyof ProfileFormValues]?.message;

    return (
      <div
        className={clsx(
          'flex min-w-0 flex-col gap-1',
          responsiveRight ? 'items-end md:items-start' : 'items-start'
        )}
      >
        <span className='truncate font-sans text-info-label font-bold text-gray-520'>{label}</span>

        {isEditing ? (
          <>
            {isEmail ? (
              // Email is always disabled/special handling
              <input
                disabled
                value={value || ''}
                className='h-[47px] w-full cursor-not-allowed rounded-10 border border-gray-200 bg-gray-450 px-3 font-jakarta text-info-value font-semibold text-gray-600'
              />
            ) : (
              <input
                type={type}
                disabled={disabled}
                min={min}
                max={max}
                {...register(field as keyof ProfileFormValues)}
                className={clsx(
                  'h-[47px] w-full rounded-10 px-3 font-jakarta text-14 font-semibold text-brand-800 transition-all md:text-info-value',
                  'border bg-white focus:outline-none',
                  errors[field as keyof ProfileFormValues]
                    ? 'border-red-500 focus:border-red-500'
                    : 'border-gray-200 focus:border-2 focus:border-accent-glow focus:ring-0',
                  responsiveRight ? 'text-right md:text-left' : 'text-left'
                )}
              />
            )}
            {errorMsg && <span className='text-xs font-medium text-red-500'>{errorMsg}</span>}
          </>
        ) : (
          <span
            className={clsx(
              'block min-h-[29px] w-full truncate border-b border-transparent pb-1 font-jakarta text-info-value-mobile font-semibold text-brand-800 md:text-info-value',
              responsiveRight ? 'text-right md:text-left' : 'text-left'
            )}
          >
            {displayValue}
          </span>
        )}
      </div>
    );
  };

  const Separator = () => <div className='my-4 h-px w-full bg-gray-420' />;

  // Derived state for display - prioritize legacy fields if available
  const displayFirstName = user?.first_name ?? '';
  const displayLastName = user?.last_name ?? '';

  return (
    <div className='relative mt-8 w-full'>
      {/* Header / Actions */}
      <div className='mb-6 flex items-center justify-between'>
        <div className='flex items-center gap-2'>
          <img
            src={accountIcon}
            alt=''
            className='size-6'
            onError={(e) => (e.currentTarget.style.display = 'none')}
          />
          <h3 className='font-sans text-section-title font-medium text-dark-100'>
            Personal Information
          </h3>
        </div>

        {isEditing ? (
          <div className='hidden items-center gap-4 md:flex'>
            <button
              onClick={handleSubmit(onSubmit)}
              disabled={isSubmitting}
              className={clsx(
                'flex items-center justify-center',
                'h-[44px] rounded-30 px-8',
                'text-16 font-bold text-white',
                'transition-transform hover:scale-105 active:scale-95',
                isSubmitting && 'cursor-not-allowed opacity-70'
              )}
              style={{
                background: `linear-gradient(0deg, #F3F4F6, #F3F4F6) padding-box, linear-gradient(90deg, #BFB0F8 0%, #E3DBFF 100%) border-box`,
                border: '2px solid transparent',
                backgroundImage: `linear-gradient(90deg, rgba(162, 137, 255, 0.99) 0%, #1A1036 100%), linear-gradient(90deg, #BFB0F8 0%, #E3DBFF 100%)`,
                backgroundOrigin: 'border-box',
                backgroundClip: 'padding-box, border-box',
              }}
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </button>

            <button
              onClick={() => {
                setIsEditing(false);
                setApiError(null);
                reset(); // Reset form on cancel
              }}
              type='button'
              disabled={isSubmitting}
              className='font-sans text-cancel-btn font-semibold text-black transition-opacity hover:opacity-70'
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            onClick={() => setIsEditing(true)}
            className='group relative flex items-center gap-1 rounded-20 border border-transparent px-3 py-1 text-14 font-bold text-dark-100 transition-all hover:bg-white'
          >
            <span className='pointer-events-none absolute inset-0 rounded-20 border border-transparent group-hover:border-brand-300 group-hover:bg-edit-hover-border group-hover:bg-clip-text group-hover:text-transparent' />
            <div className='relative flex items-center gap-1'>
              <img src={editIcon} alt='' className='size-4' /> Edit
            </div>
          </button>
        )}
      </div>

      {apiError && (
        <div className='mb-4 rounded-md bg-red-100 p-3 text-sm text-red-500'>{apiError}</div>
      )}

      {/* Form Content */}
      <div className='flex flex-col'>
        <div className='grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-6'>
          <div className='col-span-1'>
            <InputField label='First Name' field='first_name' value={displayFirstName} />
          </div>
          <div className='col-span-1'>
            <InputField label='Last Name' field='last_name' value={displayLastName} />
          </div>
          <div className='col-span-2 md:col-span-1'>
            <InputField
              label='Date of Birth'
              field='date_of_birth'
              value={user?.date_of_birth}
              type='date'
              responsiveRight={false}
              min={`${new Date().getFullYear() - 100}-01-01`}
              max={`${new Date().getFullYear() - 13}-12-31`}
            />
          </div>
        </div>
        <Separator />

        <div className='grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-6 lg:grid-cols-3'>
          <div className='md:col-span-1'>
            <InputField label='Email' field='email' value={user?.email} disabled />
          </div>
          <div className='md:col-span-1'>
            {isEditing ? (
              <div className='col-span-1'>
                <Controller
                  name='phone_number'
                  control={control}
                  render={({ field }) => (
                    <PhoneInput label='Phone' field={field} error={errors.phone_number?.message} />
                  )}
                />
              </div>
            ) : (
              <InputField
                label='Phone'
                field='phone_number'
                value={user?.phone_number}
                responsiveRight
              />
            )}
          </div>
        </div>
        <Separator />

        <div className='grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-6'>
          {isEditing ? (
            <div className='col-span-1'>
              <Controller
                name='country'
                control={control}
                render={({ field }) => (
                  <CountrySelect label='Country' field={field} error={errors.country?.message} />
                )}
              />
            </div>
          ) : (
            <InputField label='Country' field='country' value={user?.country} />
          )}
          <div className='col-span-1'>
            <InputField label='City' field='city' value={user?.city} responsiveRight />
          </div>
        </div>
        <Separator />

        <div className='grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-6 lg:grid-cols-3'>
          <div className='col-span-1'>
            <InputField label='High School' field='high_school' value={user?.high_school} />
          </div>
          <div className='col-span-1'>
            <InputField
              label='University'
              field='university'
              value={user?.university}
              responsiveRight={false}
            />
          </div>
        </div>
        <Separator />

        <div>
          <div className='flex flex-col gap-1'>
            <span className='font-sans text-info-label font-bold text-gray-520'>Bio</span>
            {isEditing ? (
              <>
                <textarea
                  {...register('bio')}
                  className={clsx(
                    'w-full rounded-10 border px-3 py-2 font-jakarta text-info-value font-semibold text-brand-800 transition-all',
                    'min-h-[100px] resize-none bg-white focus:outline-none',
                    errors.bio
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-gray-200 focus:border-2 focus:border-accent-glow focus:ring-0'
                  )}
                />
                {errors.bio && (
                  <span className='text-xs font-medium text-red-500'>{errors.bio.message}</span>
                )}
              </>
            ) : (
              <p className='min-h-[29px] font-jakarta text-info-value font-semibold leading-relaxed text-brand-800'>
                {user?.bio || '-'}
              </p>
            )}
          </div>
        </div>

        {/* Spacer for Bottom Drawer visibility */}
        {isEditing && <div className='h-[140px] w-full md:hidden' />}

        {/* Mobile Bottom Drawer for Save/Cancel */}
        <BottomDrawer isOpen={isEditing} className='md:hidden'>
          <div className='flex flex-col gap-4'>
            <Button
              onClick={handleSubmit(onSubmit)}
              disabled={isSubmitting}
              className={clsx(
                'h-[48px] w-full rounded-30 border-none shadow-lg',
                'text-16 font-bold uppercase leading-27 tracking-wide text-white'
              )}
              style={{
                background: 'linear-gradient(90deg, #A289FF 0%, #1A1036 100%)',
              }}
            >
              {isSubmitting ? 'SAVING...' : 'SAVE'}
            </Button>

            <button
              onClick={() => {
                setIsEditing(false);
                setApiError(null);
                reset();
              }}
              type='button'
              disabled={isSubmitting}
              className='w-full text-center font-sans text-16 font-bold text-brand-800'
            >
              Cancel
            </button>
          </div>
        </BottomDrawer>
      </div>
    </div>
  );
};
